import { useMemo, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import '../../shared/ui/ag-grid-theme.css'
import type {
  ColDef,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { useGridSelection } from '../../shared/hooks/useGridSelection'
import { useOrderColState } from '../orders/hooks/useOrderColState'
import type { CatalogPart } from '../../shared/api/catalog.api'

ModuleRegistry.registerModules([AllCommunityModule])

interface Props {
  parts: CatalogPart[]
  canEdit: boolean
  userId: string | undefined
  onChangeSegment: (artKey: string, category: string, partNumber: string) => void
  onRemove: (part: CatalogPart) => void
  hasNextPage: boolean
  isFetchingMore: boolean
  onLoadMore: () => void
}

// AG Grid таблица партов каталога (Артикул / Наименование / Бренд / Сегмент).
// Колонки resizable + per-user сохранение ширины/порядка/сортировки. Сегмент
// редактируется инлайн через datalist-input (id="catalog-categories" в CatalogPage).
export function CatalogPartsGrid({
  parts,
  canEdit,
  userId,
  onChangeSegment,
  onRemove,
  hasNextPage,
  isFetchingMore,
  onLoadMore,
}: Props) {
  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)
  const { saveState, onGridReady } = useOrderColState('crm_catalog_cols_v1', userId)
  const saveCb = useCallback((api: Parameters<typeof saveState>[0]) => saveState(api), [saveState])

  const colDefs = useMemo<ColDef<CatalogPart>[]>(() => {
    const cols: ColDef<CatalogPart>[] = [
      {
        field: 'article',
        headerName: 'Артикул',
        minWidth: 160,
        width: 220,
        cellRenderer: ({ data }: { data: CatalogPart }) => (
          <span className="inline-flex items-center gap-1.5">
            <Link
              to={`/catalog/part/${encodeURIComponent(data.artKey)}`}
              className="text-brand-600 hover:text-brand-700 hover:underline font-medium"
            >
              {data.article}
            </Link>
            {data.source === 'item' ? (
              <span
                className="text-[10px] px-1 py-0.5 rounded bg-gray-100 text-gray-500"
                title="Парт добавлен автоматически из позиции заявки — сегмент ещё не задан"
              >
                из заявки
              </span>
            ) : (
              data.source !== 'ref' && (
                <span
                  className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-700"
                  title="Ручной оверрайд категории"
                >
                  правка
                </span>
              )
            )}
          </span>
        ),
      },
      {
        field: 'name',
        headerName: 'Наименование',
        flex: 2,
        minWidth: 220,
        wrapText: true,
        autoHeight: true,
        valueFormatter: (p) => (p.value as string) || '—',
        cellClass: 'text-gray-600',
      },
      {
        field: 'brand',
        headerName: 'Бренд',
        minWidth: 120,
        width: 150,
        valueFormatter: (p) => (p.value as string) || '—',
      },
      {
        field: 'category',
        headerName: canEdit ? 'Сегмент (правка)' : 'Сегмент',
        minWidth: 160,
        width: 210,
        cellRenderer: canEdit
          ? ({ data }: { data: CatalogPart }) => (
              <input
                list="catalog-categories"
                defaultValue={data.category ?? ''}
                key={`${data.artKey}:${data.category ?? ''}`}
                placeholder="— без сегмента"
                onMouseDown={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  const next = e.target.value.trim()
                  if (next && next !== (data.category ?? '')) {
                    onChangeSegment(data.artKey, next, data.article)
                  }
                }}
                className="w-full px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
              />
            )
          : ({ data }: { data: CatalogPart }) => (
              <span className="text-gray-700 dark:text-gray-200">{data.category ?? '—'}</span>
            ),
      },
    ]
    if (canEdit) {
      cols.push({
        colId: 'actions',
        headerName: '',
        width: 60,
        minWidth: 50,
        maxWidth: 80,
        sortable: false,
        resizable: false,
        suppressMovable: true,
        cellClass: 'text-center',
        cellRenderer: ({ data }: { data: CatalogPart }) =>
          data.source !== 'ref' ? (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onRemove(data)
              }}
              className="text-gray-400 hover:text-red-500"
              title={
                data.source === 'item'
                  ? 'Убрать парт из каталога (вернётся при следующей заявке с ним)'
                  : 'Убрать ручной оверрайд'
              }
            >
              ✕
            </button>
          ) : null,
      })
    }
    return cols
  }, [canEdit, onChangeSegment, onRemove])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      filter: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  return (
    <>
      <div className="crm-table ag-theme-alpine rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 h-[calc(100vh-330px)]">
        <AgGridReact
          ref={gridRef}
          rowData={parts}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          getRowId={(p) => p.data.artKey}
          localeText={AG_GRID_LOCALE_RU}
          onGridReady={onGridReady}
          onCellMouseDown={onCellMouseDown}
          onCellMouseOver={onCellMouseOver}
          onDragStopped={(e: DragStoppedEvent) => saveCb(e.api)}
          onColumnResized={(e: ColumnResizedEvent) => {
            if (e.finished) saveCb(e.api)
          }}
          onSortChanged={(e: SortChangedEvent) => saveCb(e.api)}
          headerHeight={40}
          suppressDragLeaveHidesColumns
          suppressCellFocus
        />
      </div>
      {hasNextPage && (
        <div className="pt-3 text-center">
          <button
            onClick={onLoadMore}
            disabled={isFetchingMore}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            {isFetchingMore ? 'Загрузка…' : 'Загрузить ещё'}
          </button>
        </div>
      )}
    </>
  )
}
